import { v4 as uuidv4 } from 'uuid'

import { normalizeDashboards } from '../../../../utils'
import { ensureUniqueObjectIds } from '@edge/spark/utils'
import { getActiveDashboardId } from '../../../../Stores'
import type { DashboardCardDef } from '../../../../types'

import {
    ClientUserDashboardsDocument as query,
    type ClientUserDashboardsQuery,
    type MutationResolvers,
    UpdateUserDashboardsDocument as mutation,
    type UpdateUserDashboardsMutation,
} from '../../../../generated/graphql'
import { createDialog } from '@edge/spark/feature-dialog'
import type { ApolloResolverContext } from '@edge/spark/io-apollo'
import { getActiveAuctionId } from '@edge/spark/io-roles'

const addDashboardCard: MutationResolvers<ApolloResolverContext>['addDashboardCard'] = async (
    _root,
    { card, dashId, userId },
    { client },
) => {
    console.debug('io-dashboards addDashboardCardResolver Deprecated, refactor (soon)')
    console.debug('io-dashboards addDashboardCardResolver', card, dashId, userId)

    if (!client) {
        throw new Error('Missing ApolloClient')
    }

    const auctionId = getActiveAuctionId()

    if (!auctionId) {
        throw new Error('addDashboardCard: Missing AuctionId')
    }

    if (userId && !dashId) {
        throw new Error('Custom userId without dashId')
    }

    const { data } = await client.query<ClientUserDashboardsQuery>({
        query,
        variables: { userId },
        fetchPolicy: 'network-only',
    })

    const savedDashboards = normalizeDashboards(data?.dashboards)

    const activeDashboardId = getActiveDashboardId()
    const dashboardIdToUpdate = dashId || activeDashboardId
    const dashboardToUpdateIndex = savedDashboards.findIndex((d) => d.id === dashboardIdToUpdate)
    const dashboardToUpdate = savedDashboards[dashboardToUpdateIndex]

    if (!dashboardToUpdate) {
        createDialog({
            action: 'OK',
            message: `Error: Dashboard with id:${dashboardIdToUpdate} not found`,
            timeout: 5000,
        })

        return null
    }

    const cardDef = JSON.parse(card) as DashboardCardDef
    const cardWithId = {
        // @ts-expect-error Adds id only incase cardDef id is missing. Still needed?
        id: uuidv4(),
        ...cardDef,
    }
    const concatenatedCards = dashboardToUpdate.cards.concat(cardWithId)
    const cleanedCards = ensureUniqueObjectIds(concatenatedCards)
    const updatedDashboard = { ...dashboardToUpdate, cards: cleanedCards }
    const updatedDashboards = [
        ...savedDashboards.slice(0, dashboardToUpdateIndex),
        updatedDashboard,
        ...savedDashboards.slice(dashboardToUpdateIndex + 1),
    ]
    const cleanedDashboards = ensureUniqueObjectIds(updatedDashboards)
    const stringifiedUpdatedDashboards = cleanedDashboards.map((d) => JSON.stringify(d))
    const admin = userId ? { auctionId, subjectUserId: userId } : undefined

    await client.mutate<UpdateUserDashboardsMutation>({
        mutation,
        variables: { admin, dashboards: stringifiedUpdatedDashboards },
        optimisticResponse: {
            updateUser: {
                __typename: 'UserMutationResponse',
                authenticationId: userId || null,
                statusCode: 200,
                statusMessage: 'Updated dashboards.',
            },
        },
        update: (store): void => {
            if (userId) {
                store.writeData({
                    data: {
                        [`dashboards({"userId":"${userId}"})`]: stringifiedUpdatedDashboards,
                        administrator: {
                            __typename: 'Administrator',
                            [`user({"input":{"admin":{"auctionId":"${auctionId}","subjectUserId":"${userId}"}}})`]: {
                                __typename: 'User',
                                dashboards: stringifiedUpdatedDashboards,
                            },
                        },
                    },
                })
            } else {
                store.writeData({
                    data: {
                        'dashboards({})': stringifiedUpdatedDashboards,
                        user: {
                            __typename: 'User',
                            dashboards: stringifiedUpdatedDashboards,
                        },
                    },
                })
            }
        },
    })

    createDialog({
        action: 'OK',
        message: `Added card to dashboard`,
        timeout: 5000,
    })

    return true
}

export default addDashboardCard
